import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, Smartphone } from 'lucide-react';
import type { StoryPartData } from './types';
import { getSlideTemplateConfig } from './ReelsStoryConstants';

interface StoryPhonePreviewProps {
  parts: StoryPartData[];
  activeSlideIndex?: number;
  onSlideChange?: (index: number) => void;
}

export const StoryPhonePreview: React.FC<StoryPhonePreviewProps> = ({
  parts,
  activeSlideIndex = 0,
  onSlideChange,
}) => {
  const [activeSlide, setActiveSlide] = useState(activeSlideIndex);

  // Sync with external active slide
  useEffect(() => {
    setActiveSlide(activeSlideIndex);
  }, [activeSlideIndex]);

  const goTo = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(parts.length - 1, index));
    setActiveSlide(clamped);
    onSlideChange?.(clamped);
  }, [parts.length, onSlideChange]);

  if (parts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="relative mx-auto" style={{ width: '220px', aspectRatio: '9/16' }}>
          <div className="absolute inset-0 rounded-[2rem] border-[3px] border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
            <div className="text-center p-4">
              <Smartphone className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-xs text-gray-400">Dodaj slajdy, aby zobaczyć podgląd Story</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const part = parts[activeSlide] || parts[0];
  const templateConfig = getSlideTemplateConfig(part.template); // used for badge label

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Label */}
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Smartphone className="w-3.5 h-3.5" />
        <span>Podgląd Story</span>
        <span className="text-gray-300">·</span>
        <span className="font-medium">{activeSlide + 1} / {parts.length}</span>
      </div>

      {/* Phone frame */}
      <div className="relative mx-auto" style={{ width: '220px' }}>
        <div
          className="relative overflow-hidden rounded-[2rem] border-[3px] border-gray-800 bg-black shadow-xl"
          style={{ aspectRatio: '9/16' }}
        >
          {/* Notch */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-800 rounded-b-2xl z-30" />

          {/* Progress bars (Instagram-style) */}
          <div className="absolute top-6 left-3 right-3 z-20 flex gap-1">
            {parts.map((_, i) => (
              <div
                key={i}
                className="flex-1 h-[2px] rounded-full overflow-hidden bg-white/30 cursor-pointer"
                onClick={() => goTo(i)}
              >
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    i < activeSlide ? 'bg-white w-full' :
                    i === activeSlide ? 'bg-white w-full' :
                    'w-0'
                  }`}
                />
              </div>
            ))}
          </div>

          {/* Slide content */}
          <div className="absolute inset-0">
            {/* Background: media or gradient */}
            {part.mediaUrl ? (
              part.mediaType === 'video' ? (
                <video
                  key={`preview-${activeSlide}-${part.mediaUrl}`}
                  src={part.startSeconds !== undefined && part.endSeconds !== undefined
                    ? `${part.mediaUrl}#t=${part.startSeconds},${part.endSeconds}`
                    : part.mediaUrl
                  }
                  className="w-full h-full object-cover"
                  muted
                  playsInline
                  autoPlay
                  loop
                />
              ) : (
                <img
                  src={part.mediaUrl}
                  alt={`Slajd ${activeSlide + 1}`}
                  className="w-full h-full object-cover"
                />
              )
            ) : (
              <div className={`w-full h-full ${
                part.template === 'title' ? 'bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500' :
                part.template === 'quote' ? 'bg-gradient-to-br from-amber-500 via-orange-500 to-red-500' :
                part.template === 'cta' ? 'bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500' :
                part.template === 'poll' ? 'bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-500' :
                'bg-gradient-to-br from-pink-500 via-rose-500 to-orange-400'
              }`} />
            )}

            {/* Dark overlay for readability when media exists */}
            {part.mediaUrl && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            )}

            {/* Text content overlay — template-specific rendering */}
            <div className="absolute inset-0 flex flex-col justify-end p-4 pb-8">
              {renderSlideContent(part)}
            </div>
          </div>

          {/* Navigation click zones */}
          {parts.length > 1 && (
            <>
              <div
                className="absolute left-0 top-0 w-1/3 h-full z-10 cursor-pointer"
                onClick={() => goTo(activeSlide - 1)}
              />
              <div
                className="absolute right-0 top-0 w-1/3 h-full z-10 cursor-pointer"
                onClick={() => goTo(activeSlide + 1)}
              />
            </>
          )}

          {/* Template badge inside phone */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20">
            <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full bg-black/40 backdrop-blur-sm text-white/90`}>
              {templateConfig.label}
            </span>
          </div>
        </div>
      </div>

      {/* Navigation arrows below phone */}
      {parts.length > 1 && (
        <div className="flex items-center gap-3">
          <button
            onClick={() => goTo(activeSlide - 1)}
            disabled={activeSlide === 0}
            className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-30 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
          <div className="flex items-center gap-1.5">
            {parts.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`rounded-full transition-all ${
                  i === activeSlide
                    ? 'w-5 h-2 bg-gradient-to-r from-pink-500 to-orange-500'
                    : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
          <button
            onClick={() => goTo(activeSlide + 1)}
            disabled={activeSlide === parts.length - 1}
            className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-30 transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Template-specific slide rendering ────────────────────────────────

function renderSlideContent(part: StoryPartData) {
  switch (part.template) {
    case 'title':
      return (
        <div className="flex flex-col items-center justify-center text-center flex-1">
          <p className="text-white text-lg font-extrabold leading-tight drop-shadow-lg">
            {part.content || 'Nagłówek Story'}
          </p>
        </div>
      );

    case 'quote':
      return (
        <div className="flex flex-col items-center justify-center text-center flex-1 px-2">
          <div className="text-white/40 text-4xl font-serif mb-1">„</div>
          <p className="text-white text-sm italic leading-snug drop-shadow-lg">
            {part.content || 'Wyróżnione zdanie...'}
          </p>
          {part.quoteAuthor && (
            <p className="text-white/70 text-[10px] mt-2 font-medium">
              — {part.quoteAuthor}
            </p>
          )}
        </div>
      );

    case 'cta':
      return (
        <div className="space-y-3">
          <p className="text-white text-sm font-bold leading-snug drop-shadow-lg">
            {part.content || 'Call-to-action...'}
          </p>
          {/* CTA Button */}
          <div className="flex flex-col items-center gap-1.5">
            {part.ctaUrl && (
              <div className="flex items-center gap-1 text-white/60 text-[9px]">
                <ExternalLink className="w-2.5 h-2.5" />
                <span className="truncate max-w-[140px]">{part.ctaUrl}</span>
              </div>
            )}
            <div className="bg-white text-gray-900 text-[11px] font-bold px-5 py-2 rounded-full shadow-lg">
              {part.ctaButtonText || 'Sprawdź →'}
            </div>
          </div>
        </div>
      );

    case 'poll':
      return (
        <div className="space-y-2.5">
          <p className="text-white text-sm font-bold leading-snug drop-shadow-lg text-center">
            {part.content || 'Pytanie...'}
          </p>
          <div className="space-y-1.5">
            <div className="bg-white/20 backdrop-blur-sm text-white text-[11px] font-medium px-4 py-2 rounded-full text-center border border-white/30">
              {part.pollOptionA || 'Opcja A'}
            </div>
            <div className="bg-white/20 backdrop-blur-sm text-white text-[11px] font-medium px-4 py-2 rounded-full text-center border border-white/30">
              {part.pollOptionB || 'Opcja B'}
            </div>
          </div>
        </div>
      );

    case 'media':
      return part.content ? (
        <p className="text-white text-xs leading-snug drop-shadow-lg opacity-80">
          {part.content}
        </p>
      ) : null;

    default: // 'text'
      return (
        <p className="text-white text-sm leading-snug drop-shadow-lg">
          {part.content || 'Tekst Story...'}
        </p>
      );
  }
}
